// noinspection JSUnusedGlobalSymbols

import { Endpoints } from '@authentication/constants';
import { useRedirectionTools } from '@authentication/hooks';
import type {
  OtpResetCredentialsResponseType,
  OtpResetCredentialsType,
  PreResetCredentialsType,
  PreSignInType,
  SignInPreferencesType,
  SignInType,
} from '@authentication/types';
import type { AuthenticationJSON } from '@passwordless-id/webauthn/dist/esm/types';
import { useGlobalConfigStore } from '@shared/stores/useGlobalConfigStore.ts';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { HttpStatusCode } from 'axios';

type AuthenticationPasskeyType = {
  id: string;
  rawId: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string,
    userHandle?: string,
  };
  type: string;
};

const buildConfig = () => {
  const globalConfigStore = useGlobalConfigStore();

  const headerName = globalConfigStore.headerName;
  const requestToken = globalConfigStore.requestToken;

  const config = {
    headers: {} as any,
  };
  config.headers[headerName] = requestToken;

  return config as AxiosRequestConfig;
};

const postRequestAsync = async <To, Ti>(endpoint: string, command: Ti) => {
  const redirectionTools = useRedirectionTools();

  const endpointUrl = redirectionTools.getRealmEndpoint(endpoint);
  const config = buildConfig();

  return await axios.post<To>(endpointUrl, command, config);
};

export default {
  /**
   * Performs a temporal authentication for a given username and otp (temp password)
   * @param command
   */
  async authResetCredentialsAsync(command: OtpResetCredentialsType) {
    const response = await postRequestAsync<number, { username: string, otp: string }>(
      Endpoints.postAuthResetCredentialsRoute,
      {
        username: command.username,
        otp: command.otp,
      });

    return response.status === HttpStatusCode.Ok;
  },

  /**
   * Initialize a reset credentials flow for a given username. Requires an email to prevent malicious requests
   *
   * Returns a timeout value in seconds.
   * Returns 0 when the username and email are not ok
   *
   * @param command
   */
  async initResetCredentialsAsync(command: PreResetCredentialsType): Promise<OtpResetCredentialsResponseType> {
    const response = await postRequestAsync<OtpResetCredentialsResponseType, PreResetCredentialsType>(
      Endpoints.postInitResetCredentialsRoute,
      command);

    return response.data;
  },

  /**
   * Initialize a signing flow. Requires a username to get login preferences
   *
   * Returns a login preferences for a given username.
   * Returns status code 404 - NotFound when username doesn't exist
   *
   * @param username
   */
  async initSignInAsync(username: string): Promise<SignInPreferencesType> {
    const response = await postRequestAsync<SignInPreferencesType, PreSignInType>(
      Endpoints.postInitSignIn,
      {
        username: username,
      });

    return response.data;
  },

  /**
   * Initialize a signing flow with passkey. Requires a username to get a challenge key
   *
   * Returns a challenge string
   *
   * @param username
   */
  async initSignInWithPasskeyAsync(username?: string): Promise<string> {
    const response = await postRequestAsync<string, { username?: string }>(
      Endpoints.postInitSignInWithPasskey,
      {
        username: username,
      });

    return response.data;
  },

  async signInAsync(command: SignInType): Promise<AxiosResponse<boolean>> {
    const form = new URLSearchParams();
    form.set('password', command.password);
    form.set('redirectUri', command.redirectUri);
    form.set('rememberMe', command.rememberMe.toString());
    form.set('username', command.username);

    return await postRequestAsync<boolean, URLSearchParams>(
      Endpoints.postSignIn,
      form);
  },

  /**
   * Performs authentication with Passkey
   *
   * @param command
   */
  async signInWithPasskeyAsync(command: AuthenticationJSON): Promise<AxiosResponse<boolean>> {
    return await postRequestAsync<boolean, AuthenticationPasskeyType>(
      Endpoints.postSignInPasskey,
      {
        id: command.id,
        rawId: command.rawId,
        response: {
          clientDataJSON: command.response.clientDataJSON,
          authenticatorData: command.response.authenticatorData,
          signature: command.response.signature,
          userHandle: command.response.userHandle,
        },
        type: command.type,
      },
    );
  },
};
